import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, ArrowLeft, CheckCircle } from "lucide-react";
import { formatApiError } from "../utils/errorUtils";
import { APP_NAME, BRANDING } from "../config/branding";
import { trackSignupComplete } from "../utils/analytics";
import { trackMetaRegistration } from "../utils/metaPixel";
import Seo from "../components/Seo";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const promoParam = searchParams.get("promo");

  useEffect(() => {
    if (promoParam) {
      sessionStorage.setItem("founding_promo", promoParam.toUpperCase());
    }
  }, [promoParam]);

  const passwordRequirements = [
    { met: password.length >= 8, text: "At least 8 characters" },
    { met: /[A-Z]/.test(password), text: "One uppercase letter" },
    { met: /[0-9]/.test(password), text: "One number" },
  ];

  const isPasswordValid = passwordRequirements.every(req => req.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!isPasswordValid) {
      toast.error("Password doesn't meet requirements");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      
      // Track successful signup (anonymous)
      trackSignupComplete();
      trackMetaRegistration();
      
      toast.success(BRANDING.registerSuccess);
      
      // Check if user had an intended plan before signup
      const intendedPlanStr = localStorage.getItem('intended_plan');
      if (intendedPlanStr) {
        try {
          const intendedPlan = JSON.parse(intendedPlanStr);
          // Clear the intended plan from localStorage
          localStorage.removeItem('intended_plan');
          // Redirect to pricing page with plan pre-selected
          navigate('/pricing', { state: { autoCheckout: true, ...intendedPlan } });
        } catch (err) {
          // If parsing fails, just go to dashboard
          navigate("/dashboard");
        }
      } else if (promoParam || sessionStorage.getItem("founding_promo")) {
        // Came from the founding banner — send to pricing so the code carries to checkout
        navigate("/pricing");
      } else {
        // No intended plan, go to dashboard
        navigate("/dashboard");
      }
    } catch (error) {
      const errorMessage = formatApiError(error, "Registration failed. Please try again.");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex flex-col">
      <Seo title="Start Your Guided Pilot | CBAIQ" description="Create your CBAIQ account and start a guided 14-day pilot — upload one agreement, get 20 answered questions. No credit card." path="/register" noindex />
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-semibold mb-2">Create Account</h1>
            <p className="text-sm text-muted-foreground">Get started with {APP_NAME}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jane Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-card"
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-card"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-card pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <ul className="mt-2 space-y-1">
                  {passwordRequirements.map((req, i) => (
                    <li key={i} className={`text-xs flex items-center gap-1.5 ${req.met ? "text-primary" : "text-muted-foreground"}`}>
                      <CheckCircle className={`w-3 h-3 ${req.met ? "opacity-100" : "opacity-30"}`} />
                      {req.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-card"
                data-testid="confirm-password-input"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-destructive">Passwords don't match</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full btn-lift"
              disabled={loading || !isPasswordValid || password !== confirmPassword}
              data-testid="submit-register-btn"
            >
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary link-underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="disclaimer-bar py-3 px-4 text-center">
        <p>Educational tool only. Not legal advice.</p>
      </footer>
    </div>
  );
};

export default Register;
